import React, {
  useState,
  useEffect,
  useCallback,
  FormEvent,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";
import { getRoleNameById, isSuccessResponse } from "../../common/utils"
import {
  selector as verifyUserInvitationTokenSelector,
  verifyUserInvitationToken,
} from "./verifyUserInvitationToken";
import { useSelector } from "react-redux";
import { Status } from "../../common/constants";
import { useSearchParams } from "react-router-dom";
import {selector as createUserSelector, createUser, updateQueryParams } from "./createUserSlice";
import { ICreateUser, ICreateUserResponse } from "../../common/types/userTypes";
import useNotification from "../../components/notification/useNotification";

const useUserRegister = () => {
  const dispatch = useDispatch();
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const [isRegisterFailed, setIsRegisterFailed] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [phoneNo, setPhoneNo] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [isPasswordEnable, setIsPasswordEnable] = useState<boolean>(false);
  const verifyUserInvitationTokenApiResponse = useSelector(
    verifyUserInvitationTokenSelector
  );
  const createUserResponseApiResponse = useSelector(createUserSelector);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const { showSuccessNotification, showWarningNotification } = useNotification();

  useEffect(() => {
    const token = searchParams?.get("token");
    dispatch(updateQueryParams(token));
  }, [searchParams, dispatch]);

  const handleClose = () => {
    setEmail("");
    setUserName("")
    setPhoneNo("");
    setRole("");
    setPassword("");
    setConfirmPassword("");
    setIsRegistered(true);
  };

  useEffect(() => {
    if (
      verifyUserInvitationTokenApiResponse.status === Status.PENDING
    ) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [ verifyUserInvitationTokenApiResponse.status]);

  useEffect(() => {
    if (createUserResponseApiResponse.status === Status.PENDING) {
      setIsLoading(true);
    } 
  }, [createUserResponseApiResponse.status])

  const handleVerifyUserInvitationToken = useCallback(async () => {
    const verificationToken = new URLSearchParams(window.location.search).get(
      "token"
    );
    if (verificationToken && !isVerified) {
      dispatch(verifyUserInvitationToken(verificationToken));
    }
  }, [dispatch, isVerified]);

  const validatePassword = (value: string): void => {
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&_])[A-Za-z\d@$!%*?&_]{8,}$/;
    if (!passwordRegex.test(value)) {
      setPasswordError(
        "Password must be at least 8 characters, include a number, and a special character."
      );
    } else {
      setPasswordError("");
    }
  };

  const validatePhoneNo = (value: string): void => {
    const isAllDigits = value.split('').every(char => char >= '0' && char <= '9');
  
    if (!isAllDigits) {
      setPhoneError("Phone number must contain digits only.");
    } else {
      setPhoneError("");
    }
  };
  

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const handlePhoneNoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setPhoneNo(value);
    validatePhoneNo(value);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value !== password) {
      setConfirmPasswordError("Passwords do not match.");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleUserRegisteration = (e) => {
    e.preventDefault();

    if (isPasswordEnable) {
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

     if (!trimmedPassword) {
      setPasswordError("Password is required.");
    }

    if (!trimmedConfirmPassword) {
      setConfirmPasswordError("Confirm Password is required.");
    }

    if (!trimmedPassword || !trimmedConfirmPassword) {
      return;
    }

    if (passwordError) {
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return;
    }
  }


    const payload: ICreateUser = {
      email: verifyUserInvitationTokenApiResponse.userDetails.email,
      name: verifyUserInvitationTokenApiResponse.userDetails.name,
      phoneNo: phoneNo,
      role: verifyUserInvitationTokenApiResponse.userDetails.role,
      groupId: verifyUserInvitationTokenApiResponse.userDetails.groupId,
      organizationId: verifyUserInvitationTokenApiResponse.userDetails.organizationId,
      organizationName: verifyUserInvitationTokenApiResponse.userDetails.organizationName,
      isPasswordEnable: isPasswordEnable,
      password: password,
    }
    const token = new URLSearchParams(window.location.search).get("token");
    dispatchCreateUser(createUser, payload);
  }

  const dispatchCreateUser = (
    action,
    payload: ICreateUser
  ) => {
    try {
      dispatch(action(payload)).then((response) => {
        const userResponse: ICreateUserResponse = response.payload;
        if (isSuccessResponse(response.payload)) {
          showSuccessNotification(response.payload.message);
          setIsLoading(false);
          setIsRegistered(true);
        } else {
          showWarningNotification(response.payload.response?.data?.message);
          setIsLoading(false);
          setIsRegisterFailed(true);
        }
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };
  

  const isTokenVerificationFailed = useMemo((): boolean => {
    return verifyUserInvitationTokenApiResponse.status === Status.FAILED;
  }, [verifyUserInvitationTokenApiResponse.status]);

  useEffect(() => {
    handleVerifyUserInvitationToken();
  }, [handleVerifyUserInvitationToken]);

  useEffect(() => {
    if (verifyUserInvitationTokenApiResponse.status === Status.SUCCESS) {    
      setEmail(verifyUserInvitationTokenApiResponse.userDetails.email);
      setUserName(verifyUserInvitationTokenApiResponse.userDetails.name);      
      setRole(getRoleNameById(verifyUserInvitationTokenApiResponse.userDetails.role));
      setIsPasswordEnable(verifyUserInvitationTokenApiResponse.userDetails.isPasswordEnable);
      setIsVerified(true);
    }
  }, [verifyUserInvitationTokenApiResponse.status]);

  return {
    isTokenVerificationFailed,
    handleUserRegisteration,
    isRegistered,
    password,
    userName,
    email,
    role,
    phoneNo,
    confirmPassword,
    isPasswordEnable,
    passwordError,
    confirmPasswordError,
    phoneError,
    setPassword,
    setUserName,
    setEmail,
    setPhoneNo,
    setRole,
    setConfirmPassword,
    setIsPasswordEnable,
    setPasswordError,
    setConfirmPasswordError,
    setPhoneError,
    isVerified,
    isRegisterFailed,
    isLoading,
    createUserResponseApiResponse,
    validatePassword,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handlePhoneNoChange,
  };
};

export default useUserRegister;
